import { useState, useCallback, useRef } from "react";
import axios from "axios";

/**
 * 🚀 最終版APIフック - 完全に安全な設計
 * StrictMode対応 + ESLint警告解消 + 論理削除対応
 * 製品選択の状態管理は useClientListState に移譲
 */
export const useApi = (navigate, pageSize, page, setPage) => {
  const [clients, setClients] = useState([]);
  const [groups, setGroups] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);
  
  // 🔹 初期化フラグを追加
  const [isInitialized, setIsInitialized] = useState(false);
  
  // 🚀 StrictMode対応：初回ロード制御を強化
  const hasInitialized = useRef(false);

  // 🚀 製品データの安全性チェック関数
  const filterActiveProducts = useCallback((productsArray) => {
    if (!Array.isArray(productsArray)) return [];
    
    return productsArray.filter(product => 
      product && 
      product.id && 
      product.name && 
      product.active !== false // 論理削除された製品を除外
    );
  }, []);

  /**
   * 🚀 安全版：fetchClients - 依存配列を最小化
   */
  const fetchClients = useCallback(async (
    currentPage = 0, 
    search = "", 
    groupIds = [], 
    productId = null, 
    sortBy = "id_asc",
    filters = {}
  ) => {
    const token = sessionStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      const params = new URLSearchParams({
        page: currentPage.toString(),
        size: pageSize.toString(),
        sort: sortBy
      });
      
      if (search.trim()) {
        params.append('search', search.trim());
      }
      
      const sanitizedGroupIds = groupIds || [];
      if (sanitizedGroupIds.length > 0) {
        sanitizedGroupIds.forEach(id => params.append('groupIds', id.toString()));
      }
      
      if (productId && productId !== 'all') {
        params.append('productId', productId.toString());
      }
      
      // 残量フィルター（「すべて」以外の場合のみ）
      if (productId && productId !== 'all') {
        if (filters.remainingMin !== undefined && filters.remainingMin !== '') {
          params.append('remainingMin', filters.remainingMin.toString());
        }
        if (filters.remainingMax !== undefined && filters.remainingMax !== '') {
          params.append('remainingMax', filters.remainingMax.toString());
        }
        if (filters.ratioMin !== undefined && filters.ratioMin !== '') {
          params.append('ratioMin', filters.ratioMin.toString());
        }
        if (filters.ratioMax !== undefined && filters.ratioMax !== '') {
          params.append('ratioMax', filters.ratioMax.toString());
        }
      }

      // includeUnregistered パラメータ
      if (filters.includeUnregistered !== undefined) {
        params.append('includeUnregistered', filters.includeUnregistered.toString());
      } else {
        params.append('includeUnregistered', 'true');
      }

      const response = await axios.get(`/api/clients?${params.toString()}`, { headers });
      const data = response.data;
      
      const clientList = data.content || data.clients || [];
      
      setClients(clientList);
      setPage(data.number !== undefined ? data.number : data.currentPage || 0);
      setTotalPages(data.totalPages || 0);
      setTotalElements(data.totalElements || 0);
      setHasNext(!data.last);
      setHasPrevious(!data.first);
      setError(null);
      
    } catch (err) {
      setError(`データの取得に失敗しました: ${err.message || "不明なエラー"}`);
      
      if (err.response && err.response.status === 401) {
        alert("認証の有効期限が切れました。再度ログインしてください。");
        sessionStorage.removeItem("token");
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, pageSize]); // 🔹 必要最小限の依存配列

  /**
   * 🚀 最終版：初期データ取得 - StrictMode完全対応 + 論理削除対応
   * 製品選択の初期化も外部から制御可能
   */
  const fetchInitialData = useCallback(async (initialProductId = null) => {
    // StrictMode対応：既に初期化済みの場合はスキップ
    if (hasInitialized.current) {
      return;
    }
    
    const token = sessionStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      hasInitialized.current = true; // 🚀 実行前にフラグを設定
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      const [groupRes, productRes] = await Promise.all([
        axios.get("/api/client_groups", { headers }),
        axios.get("/api/products", { headers }),
      ]);
      
      setGroups(groupRes.data);
      
      // 🚀 製品データの安全性チェック追加
      const rawProducts = productRes.data || [];
      const safeProducts = filterActiveProducts(rawProducts);
      
      
      setProducts(safeProducts);
      
      // 🚀 初期化は外部から制御（状態復元時に使用）
      let finalProductId = initialProductId;
      
      // 外部からの指定がない場合のみ、既存ロジックを使用
      if (finalProductId === null) {
        const urlParams = new URLSearchParams(window.location.search);
        const productIdFromUrl = urlParams.get('productId');
        
        if (productIdFromUrl === 'all') {
          finalProductId = 'all';
        } else if (productIdFromUrl && safeProducts.find(p => p.id === Number(productIdFromUrl))) {
          finalProductId = Number(productIdFromUrl);
        } else if (safeProducts.length) {
          if (safeProducts.length === 1) {
            finalProductId = safeProducts[0].id;
          } else {
            finalProductId = 'all';
          }
        } else {
          // 🚀 製品が存在しない場合
          finalProductId = null;
        }
      }
      
      // 🚀 選択された製品IDが有効かチェック
      if (finalProductId !== 'all' && finalProductId && !safeProducts.find(p => p.id === finalProductId)) {
        finalProductId = safeProducts.length > 0 ? 'all' : null;
      }
      
      // 🚀 初回顧客データ取得
      await fetchClients(0, "", [], finalProductId, "id_asc", { includeUnregistered: true });
      
      setIsInitialized(true);
      
      return finalProductId; // 🚀 決定した製品IDを返す
      
    } catch (err) {
      setError(`初期データの取得に失敗しました: ${err.message || "不明なエラー"}`);
      
      if (err.response && err.response.status === 401) {
        alert("認証の有効期限が切れました。再度ログインしてください。");
        sessionStorage.removeItem("token");
        navigate("/login");
      }
      
      // エラー時はフラグをリセット
      hasInitialized.current = false;
    } finally {
      setLoading(false);
    }
  }, [navigate, fetchClients, filterActiveProducts]); // 🔹 正しい依存配列

  // 🚀 URLパラメータ変更の監視は不要（状態管理が移譲されたため）

  return {
    clients,
    groups,
    products,
    loading,
    error,
    totalPages,
    totalElements,
    hasNext,
    hasPrevious,
    fetchClients,
    fetchInitialData,
    isInitialized
  };
};